import { createContext, useContext, useLayoutEffect, useState, type ReactNode } from 'react';
import { usePortalOverlay } from '../../components/PortalOverlayProvider';
import type { PortalApplication, PortalIdentity } from '../../model/portalModels';
import { resolveApplicationRuntimeState } from '../../navigation/applicationSelection';
import { resolveFixedPortalLeaf } from '../../navigation/portalNavigation';
import MobileShellView from '../views/MobileShellView';
import { applicationModel } from '../workbench/workbenchModels';
import { activateMobileStyles } from './mobileLifecycle';
import { routeFromPath } from './navigation';

const ApplicationContext = createContext({ openSwitcher: () => {}, closeSwitcher: () => {}, switcherOpen: false, selectApplication: (_code: string) => {} });
export const useMobileApplications = () => useContext(ApplicationContext);
const MobileLayoutContext = createContext(false);
// Page views must consume the effective layout, never raw matchMedia during managed work.
export const useMobilePortalLayout = () => useContext(MobileLayoutContext);

export function MobileApplicationProvider(props: {
  applications: PortalApplication[]; pathname: string; applicationSwitchingTo?: string; mobile: boolean;
  onSelectApplication: (application: PortalApplication) => void;
  children: ReactNode;
}) {
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const { message } = usePortalOverlay();
  useLayoutEffect(() => setSwitcherOpen(false), [props.pathname]);
  const selectApplication = (code: string) => {
    if (props.applicationSwitchingTo) return;
    const application = props.applications.find(item => item.appCode === code);
    if (!application) { message.warning('当前应用无访问权限'); return; }
    const state = resolveApplicationRuntimeState(application.status);
    if (state !== 'ready') {
      message.warning(state === 'maintenance' ? `${application.appName}正在维护` : `${application.appName}当前不可用`);
      return;
    }
    setSwitcherOpen(false);
    props.onSelectApplication(application);
  };
  return <MobileLayoutContext.Provider value={props.mobile}><ApplicationContext.Provider value={{ openSwitcher: () => setSwitcherOpen(true), closeSwitcher: () => setSwitcherOpen(false), switcherOpen, selectApplication }}>{props.children}</ApplicationContext.Provider></MobileLayoutContext.Provider>;
}

export function MobilePortalEntry(props: {
  identity: PortalIdentity; applications: PortalApplication[]; pathname: string;
  currentApplication?: PortalApplication; applicationSwitchingTo?: string;
  onNavigate: (path: string) => void;
  onSelectApplication: (application: PortalApplication) => void;
  children: ReactNode;
}) {
  const applications = useMobileApplications();
  useLayoutEffect(activateMobileStyles, []);
  const route = !resolveFixedPortalLeaf(props.pathname) && props.currentApplication
    ? { kind: 'business' as const, appCode: props.currentApplication.appCode, sectionCode: '' }
    : routeFromPath(props.pathname);
  const title = resolveFixedPortalLeaf(props.pathname)?.label ?? props.currentApplication?.appName ?? '工作台';
  return <MobileShellView route={route} title={title} avatarName={props.identity.actorName}
      mode="persistent" noticeCount={0} apps={props.applications.map(applicationModel)}
      embeddedApp={false} switcherOpen={applications.switcherOpen}
      onSwitcherOpen={applications.openSwitcher} onSwitcherClose={applications.closeSwitcher}
      onSwitchApp={applications.selectApplication} onNavigate={props.onNavigate}
      onCloseBusiness={() => props.onNavigate('/portal/workbench')}>
      {props.children}
      {props.applicationSwitchingTo ? <div className="portal-application-switch-overlay" role="status" aria-busy="true"><div className="portal-application-switch-overlay__panel"><strong>{props.applicationSwitchingTo}</strong><span>正在加载应用</span></div></div> : null}
    </MobileShellView>;
}
